import { memo, useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { motion, type Transition, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import crownJackpotArt from '../../../../../assets/crown-jackpot-thumbnail.webp';
import { SocialCoin } from '../../../../../components/SocialCoin/SocialCoin';
import { SpriteAnimation } from '../../../../../components/SpriteAnimation/SpriteAnimation';
import type { LoadedSprite } from '../../../../../components/SpriteAnimation/types';
import { SweepCoin } from '../../../../../components/SweepCoin/SweepCoin';
import { useFitText } from '../../../../../hooks/useFitText';
import { useFullyInView } from '../../../../../hooks/useFullyInView';
import { formatNumber } from '../../../../../utils/numberFormat';
import { SPRITE_DURATION_MS } from '../constants';
import type { TileGeometry } from '../hooks/useTileGeometry';
import type { WinnerTileVM } from '../types';

import styles from './CrownJackpotTile.module.scss';

// The tile waits as the Crown Jackpot banner. Once fully in view the jackpot's coin burst plays over it;
// when the burst finishes (as the generic jackpot tile swaps on its burst's end), the banner fades out,
// the art card slides in from the left, then the nickname and the win amount slide out from behind the
// card and the coin pops in. Timings are seconds from that swap, measured from the design recording (60 fps,
// the swap starts at frame 63); eases fitted with fit_ease.
const at = (frame: number) => (frame - 63) / 60;
// The banner's background darkens f63 → f71 and its artwork lingers until f76; as one image it fades over
// the whole span (hand-set ease between the two).
const BANNER_FADE: Transition = { delay: at(63), duration: at(76) - at(63), ease: [0.2, 0.5, 0.6, 1] };
const CARD_SLIDE: Transition = { delay: at(77.8), duration: at(104.2) - at(77.8), ease: [0.319, 0.566, 0.003, 1] };
const INFO_SLIDE: Transition = {
  x: { delay: at(92.7), duration: at(105.5) - at(92.7), ease: 'easeInOut' },
  // Hidden behind the card until it slides out; no fade in the recording.
  opacity: { delay: at(92.7), duration: 0 },
};
const COIN_POP: Transition = { delay: at(105), duration: at(116) - at(105), ease: 'easeOut' };
// Room the coin takes in the bottom row; it grows from 0 so the text is pushed right as it pops in.
const COIN_SLOT_PX = 20;
// Space beside the card left for the amount: the tile's paddings, the gap and the coin.
const AMOUNT_TEXT_RESERVED_PX = 38;
// Where the card waits before sliding in: just past the tile's left edge.
const CARD_HIDDEN_GAP_PX = 6;

interface CrownJackpotTileProps {
  tile: WinnerTileVM;
  geometry: TileGeometry;
  viewportRef: React.RefObject<HTMLDivElement>;
  // The generic jackpot's coin burst for the win's coin, shared by the conveyor.
  sprite: LoadedSprite;
  // The belt's settled copy starts finished and never plays the intro.
  playIntro?: boolean;
  onSelect?: (gameId: number) => void;
}

export const CrownJackpotTile = memo(
  ({ tile, geometry, viewportRef, sprite, playIntro = true, onSelect }: CrownJackpotTileProps) => {
    // Subscribes to a language switch so the memoized tile re-formats its amount.
    useTranslation();
    const tileRef = useRef<HTMLDivElement>(null);
    const reduceMotion = useReducedMotion();
    const fullyVisible = useFullyInView(tileRef, viewportRef);
    const isSweep = tile.mode === 'SC';
    const amountText = formatNumber(tile.amount);
    const fitAmountRef = useFitText(
      geometry.amountMaxFont,
      7,
      geometry.tileWidth - geometry.thumbWidth - AMOUNT_TEXT_RESERVED_PX,
      amountText,
    );

    // idle: the banner, waiting to be fully in view · burst: the coin burst over the banner · reveal: the
    // swap to the card and text, once the burst has finished · settled: the end state, no animation.
    const [phase, setPhase] = useState<'idle' | 'burst' | 'reveal' | 'settled'>(
      playIntro && !reduceMotion ? 'idle' : 'settled',
    );
    // Animate only when it actually plays; the settled copy and reduced motion render the end state.
    const animated = useRef(phase !== 'settled').current;

    useEffect(() => {
      if (phase === 'idle' && fullyVisible && sprite.ready) setPhase('burst');
    }, [phase, fullyVisible, sprite.ready]);

    // Stable, so SpriteAnimation (which restarts when its callback changes) is never reset mid-burst.
    const handleBurstEnd = useCallback(() => setPhase('reveal'), []);

    // Everything before the swap shows the banner with the card and text hidden.
    const waiting = phase === 'idle' || phase === 'burst';
    const cardHiddenX = -(geometry.thumbWidth + CARD_HIDDEN_GAP_PX);

    return (
      <div
        ref={tileRef}
        className={classNames(styles.tile, { [styles.notClickable]: !onSelect })}
        onClick={onSelect ? () => onSelect(tile.gameId) : undefined}
      >
        <motion.div
          animate={{ x: waiting ? cardHiddenX : 0 }}
          className={styles.card}
          initial={animated ? { x: cardHiddenX } : false}
          transition={CARD_SLIDE}
        >
          <img alt="crown jackpot" className={styles.art} draggable={false} src={crownJackpotArt} />
        </motion.div>

        <motion.div
          animate={waiting ? { x: '-100%', opacity: 0 } : { x: 0, opacity: 1 }}
          className={styles.info}
          initial={animated ? { x: '-100%', opacity: 0 } : false}
          transition={INFO_SLIDE}
        >
          <span className={styles.nickname}>{tile.nickname}</span>
          <span className={styles.amountRow}>
            <motion.span
              animate={waiting ? { maxWidth: 0, scale: 0 } : { maxWidth: COIN_SLOT_PX, scale: 1 }}
              className={styles.coinSlot}
              initial={animated ? { maxWidth: 0, scale: 0 } : false}
              transition={COIN_POP}
            >
              {isSweep ? (
                <SweepCoin alt="sweep coin" className={styles.coin} />
              ) : (
                <SocialCoin alt="social coin" className={styles.coin} />
              )}
            </motion.span>
            <span ref={fitAmountRef} className={classNames(styles.amount, isSweep ? styles.amountSc : styles.amountCc)}>
              {amountText}
            </span>
          </span>
        </motion.div>

        {phase !== 'settled' && (
          <motion.span
            animate={{ opacity: waiting ? 1 : 0 }}
            className={styles.banner}
            data-testid="crown-jackpot-banner"
            initial={false}
            transition={BANNER_FADE}
          />
        )}

        {phase === 'burst' && (
          <SpriteAnimation
            isPlaying
            className={styles.sprite}
            frameInterval={sprite.frameCount > 0 ? SPRITE_DURATION_MS / sprite.frameCount : 0}
            initialSprite={sprite}
            sprite={sprite}
            onCycleComplete={handleBurstEnd}
          />
        )}
      </div>
    );
  },
);

CrownJackpotTile.displayName = 'CrownJackpotTile';
